import {RelativeTime} from '@primer/react'
import type React from 'react'

import {resolveAgentProfileForTask} from '../../agents/resolve-agent-profile'
import {getReadableSessionState} from '../../types/session'
import type {UITask} from '../../types/task'
import {getEffectivePrState} from '../../utils/task-artifact-helpers'
import {getLeadingVisualStatusLabel} from '../../utils/get-leading-visual-icon-attributes'

const TASK_BUCKET_LABELS = ['Today', 'This week', 'Older'] as const

export type TaskBucketLabel = (typeof TASK_BUCKET_LABELS)[number]

export interface TaskBucket<T> {
  label: TaskBucketLabel
  tasks: T[]
}

export function getTaskBucketTestId(label: TaskBucketLabel): string {
  return `task-bucket-${label.toLowerCase().replace(/\s+/g, '-')}`
}

function getStartOfDay(date: Date): Date {
  const startOfDay = new Date(date)
  startOfDay.setHours(0, 0, 0, 0)
  return startOfDay
}

function getStartOfWeek(date: Date): Date {
  const startOfWeek = getStartOfDay(date)
  startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay())
  return startOfWeek
}

function getTaskBucketLabel(updatedAt: string, now: Date): TaskBucketLabel {
  const updatedAtDate = new Date(updatedAt)

  if (Number.isNaN(updatedAtDate.getTime())) {
    return 'Older'
  }

  const startOfToday = getStartOfDay(now)
  if (updatedAtDate >= startOfToday) {
    return 'Today'
  }

  const startOfCurrentWeek = getStartOfWeek(now)
  if (updatedAtDate >= startOfCurrentWeek) {
    return 'This week'
  }

  return 'Older'
}

export function sortTasksByUpdatedAtDesc<T extends Pick<UITask, 'latest_session_updated_at'>>(tasks: T[]): T[] {
  return [...tasks].sort((leftTask, rightTask) => {
    return (
      new Date(rightTask.latest_session_updated_at).getTime() - new Date(leftTask.latest_session_updated_at).getTime()
    )
  })
}

export function groupTasksByUpdatedAt<T extends Pick<UITask, 'latest_session_updated_at'>>(
  tasks: T[],
  now: Date = new Date(),
): Array<TaskBucket<T>> {
  const buckets = new Map<TaskBucketLabel, T[]>(TASK_BUCKET_LABELS.map(label => [label, []]))

  for (const task of sortTasksByUpdatedAtDesc(tasks)) {
    buckets.get(getTaskBucketLabel(task.latest_session_updated_at, now))?.push(task)
  }

  return TASK_BUCKET_LABELS.map(label => ({label, tasks: buckets.get(label) ?? []})).filter(
    bucket => bucket.tasks.length > 0,
  )
}

export function TaskBucketHeader({label}: {label: TaskBucketLabel}) {
  return (
    <li data-testid={getTaskBucketTestId(label)}>
      <div className="m-0 px-2 py-2 color-fg-muted text-small text-semibold">{label}</div>
    </li>
  )
}

function getPRStatusLabel(task: UITask): string | undefined {
  const {pull} = task
  if (!pull) return undefined
  if (pull.state === 'merged') return 'Merged'
  if (pull.state === 'closed') return 'Closed'
  if (pull.state === 'open' && pull.reviewable_state === 'draft') return 'Draft'
  if (pull.inMergeQueue) return 'Queued to merge'
  return undefined
}

export function getTaskSecondaryText(task: UITask): React.ReactNode {
  if (task.isOptimistic) {
    return 'Initializing...'
  }

  const agentProfile = resolveAgentProfileForTask(task)

  // Managed Cloud sessions should show their session status as secondary label.
  const isSandboxSession = task.current_environment?.kind === 'managed-sandbox'

  if (!isSandboxSession && agentProfile.interactiveSession && agentProfile.secondaryLabel) {
    const sessionLabel = task.remote_steerable
      ? `Remote ${agentProfile.secondaryLabel} session`
      : `${agentProfile.secondaryLabel} session (read only)`
    return (
      <>
        <span className="sr-only">
          {getLeadingVisualStatusLabel(
            getEffectivePrState(task.artifact_state, task.pull),
            task.pull?.inMergeQueue,
            task.latest_session_state,
            !!task.archived_at,
          )}
          .{' '}
        </span>
        {sessionLabel}
        {' · '}
        <RelativeTime format="micro" datetime={task.latest_session_updated_at} />
      </>
    )
  }

  const isActiveSession = task.latest_session_state === 'in_progress' || task.latest_session_state === 'queued'
  const statusText = isActiveSession
    ? getReadableSessionState(task.latest_session_state, {hasPull: !!task.pull})
    : getPRStatusLabel(task) || getReadableSessionState(task.latest_session_state, {hasPull: !!task.pull})

  return (
    <>
      {statusText}
      {task.pull?.number ? ` · #${task.pull.number}` : ''}
      {' · '}
      <RelativeTime format="micro" datetime={task.latest_session_updated_at} />
    </>
  )
}
